import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, ChevronLeftIcon, ExclamationTriangleIcon, InboxIcon, PlusIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ActionButton } from '../../../components/buttons/ActionButton';
import { Loader } from '../../../components/loading/Loader';
import { Panel } from '../../../components/panels/Panel';
import { ZoomableImageViewport } from '../components/ZoomableImageViewport';
import {
  createEvaluationDataset,
  getEvaluationAvailability,
  getEvaluationDataset,
  getEvaluationDatasetMetadata,
  getEvaluationDatasets,
  getEvaluationPrecision,
  isEvaluationDataset,
  saveEvaluationImage,
} from '../../../services/evaluation/evaluation-service';

const getCandidateId = (candidate) => candidate && typeof candidate === 'object' ? candidate.id : candidate;
const mergeCandidateTimestamps = (current, candidates) => {
  const next = { ...current };
  candidates.forEach((candidate) => {
    const id = getCandidateId(candidate);
    if (id !== null && id !== undefined && Number.isFinite(candidate?.timestamp)) next[String(id)] = candidate.timestamp;
  });
  return next;
};
const mergeCandidateDetails = (current, candidates) => {
  const next = { ...current };
  candidates.forEach((candidate) => {
    const id = getCandidateId(candidate);
    if (id === null || id === undefined || typeof candidate !== 'object') return;
    next[String(id)] = { camera: candidate.camera ?? next[String(id)]?.camera ?? null, line: candidate.line ?? next[String(id)]?.line ?? null };
  });
  return next;
};
const formatDetectionDate = (timestamp, timeZone) => Number.isFinite(timestamp) ? new Date(timestamp * 1000).toLocaleString('es-ES', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }) : 'Fecha no disponible';
const formatPrecision = (value) => value === null || value === undefined ? '--' : `${value.toFixed(1)}%`;
const getDefectName = (options, type) => type === null || type === undefined ? 'Defecto desconocido' : options.find((option) => Number(option.id) === Number(type))?.name ?? `Defecto ${type}`;
const getDatasetImages = (dataset) => [...(dataset?.dataset_images ?? [])].sort((a, b) => a.id - b.id);
const getDatasetTitle = (name) => name?.replace(/^Evaluaci[oó]n\s*[-–—·:]\s*/i, '') ?? '';
const parseSegmentationMask = (text) => {
  const values = text.trim().split(/\s+/).map(Number);
  if (values.length < 6 || values.length % 2 !== 0 || values.some((value) => !Number.isFinite(value) || value < 0 || value > 1)) return null;
  return Array.from({ length: values.length / 2 }, (_, index) => [values[index * 2], values[index * 2 + 1]]);
};

const voteButtonClass = 'inline-flex h-10 w-36 shrink-0 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const EvaluationImage = ({ src, alt, showMask, onMaskAvailabilityChange }) => {
  const [status, setStatus] = useState('loading');
  const [retry, setRetry] = useState(0);
  const [imageSize, setImageSize] = useState(null);
  const [mask, setMask] = useState(null);
  const imageUrl = !src || retry === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${retry}`;

  useEffect(() => {
    setMask(null);
    onMaskAvailabilityChange(false);
    const maskUrl = imageUrl?.replace(/\.jpe?g(?=[?#]|$)/i, '.txt');
    if (!maskUrl || maskUrl === imageUrl) return;
    const controller = new AbortController();
    fetch(maskUrl, { signal: controller.signal }).then((response) => {
      if (!response.ok) throw new Error(`Unable to load segmentation mask: ${response.status}`);
      return response.text();
    }).then((text) => {
      if (controller.signal.aborted) return;
      const parsedMask = parseSegmentationMask(text);
      setMask(parsedMask);
      onMaskAvailabilityChange(!!parsedMask);
    }).catch(() => {});
    return () => controller.abort();
  }, [imageUrl, onMaskAvailabilityChange]);

  const handleRetry = () => { setStatus('loading'); setImageSize(null); setRetry((value) => value + 1); };
  const polygonPoints = mask && imageSize ? mask.map(([x, y]) => `${x * imageSize.width},${y * imageSize.height}`).join(' ') : null;

  return (
    <div className="relative h-full min-h-0 w-full overflow-hidden rounded-md bg-perception-black-900">
      <ZoomableImageViewport resetKey={imageUrl}>
        {src && <img key={imageUrl} src={imageUrl} alt={alt} draggable={false} className={`absolute inset-0 h-full w-full object-contain ${status === 'ready' ? '' : 'hidden'}`} onLoad={(event) => { setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setStatus('ready'); }} onError={() => setStatus('missing')}/>}
        {status === 'ready' && showMask && polygonPoints && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <polygon points={polygonPoints} fill="rgba(239, 68, 68, 0.32)" stroke="rgba(248, 113, 113, 0.95)" strokeWidth="2" vectorEffect="non-scaling-stroke"/>
          </svg>
        )}
      </ZoomableImageViewport>
      {status !== 'ready' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          {status === 'loading' && src ? <ArrowPathIcon className="size-10 animate-spin text-perception-gray-700"/> : <ExclamationTriangleIcon className="size-12 text-perception-gray-700"/>}
          <div className="text-sm text-perception-gray-500">{status === 'loading' && src ? 'Cargando imagen...' : 'La imagen estará disponible pronto.'}</div>
          {(status === 'missing' || !src) && <ActionButton text="Reintentar" Icon={ArrowPathIcon} color="secondary" size="sm" onClick={handleRetry}/>}
        </div>
      )}
    </div>
  );
};

const EvaluationThumbnail = ({ image, index, selected, src, onClick }) => {
  const state = image.state;
  const stateClass = state === 'validated' ? 'bg-perception-success-600/90' : state === 'error' ? 'bg-red-600/90' : 'bg-perception-black-500/85';

  return (
    <button type="button" onClick={onClick} className={`relative h-full w-36 min-w-36 overflow-hidden rounded-md border-2 bg-perception-black-700 transition-colors ${selected ? 'border-perception-blue' : 'border-transparent hover:border-perception-gray-700'}`}>
      <img loading="lazy" src={src} alt={`Imagen ${index + 1}`} draggable={false} className="h-full w-full object-cover"/>
      <span className="absolute left-2 top-2 rounded bg-perception-black-500/90 px-2 py-1 text-xs text-white">#{index + 1}</span>
      <span className={`absolute bottom-2 right-2 flex items-center gap-1 rounded px-2 py-1 text-xs text-white ${stateClass}`}>
        {state === 'validated' ? <><CheckCircleIcon className="size-4"/>Correcto</> : state === 'error' ? <><XCircleIcon className="size-4"/>Incorrecto</> : 'Pendiente'}
      </span>
    </button>
  );
};

const EvaluationScore = ({ title, images, globalPrecision, onBack }) => {
  const { correct, incorrect, evaluated, precision } = getEvaluationPrecision(images);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 p-6 text-center">
      <CheckCircleIcon className="size-20 text-perception-success-600"/>
      <div>
        <div className="text-xl font-semibold text-white">Evaluación completada</div>
        <div className="mt-2 text-sm text-perception-gray-500">{title}</div>
      </div>
      <div className="grid w-full max-w-2xl grid-cols-2 gap-3 xl:grid-cols-4">
        {[{ label: 'Evaluadas', value: evaluated }, { label: 'Correctas', value: correct }, { label: 'Incorrectas', value: incorrect }, { label: 'Precisión', value: formatPrecision(precision) }].map((item) => (
          <div key={item.label} className="rounded-md bg-perception-black-700 p-4">
            <div className="text-2xl font-semibold text-white">{item.value}</div>
            <div className="mt-2 text-xs text-perception-gray-500">{item.label}</div>
          </div>
        ))}
      </div>
      <div className="text-sm text-perception-gray-500">Precisión global del defecto: <span className="font-semibold text-white">{formatPrecision(globalPrecision)}</span></div>
      <ActionButton text="Ver imágenes" color="secondary" onClick={onBack}/>
    </div>
  );
};

export const EvaluationPage = ({ clientId, defectOptions, loadCandidateIds, getImageUrl, timeZone }) => {
  const [datasets, setDatasets] = useState([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const [selectedDefectType, setSelectedDefectType] = useState(String(defectOptions[0]?.id ?? ''));
  const [selectedCount, setSelectedCount] = useState(10);
  const [candidateIds, setCandidateIds] = useState([]);
  const [candidateTimestamps, setCandidateTimestamps] = useState({});
  const [candidateDetails, setCandidateDetails] = useState({});
  const [availability, setAvailability] = useState(null);
  const [datasetAvailability, setDatasetAvailability] = useState(null);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [candidatesError, setCandidatesError] = useState('');
  const [showMask, setShowMask] = useState(true);
  const [maskAvailable, setMaskAvailable] = useState(false);
  const thumbnailsRef = useRef(null);

  const selectedDataset = datasets.find((dataset) => dataset.id === selectedDatasetId) ?? null;
  const selectedMetadata = getEvaluationDatasetMetadata(selectedDataset);
  const images = useMemo(() => getDatasetImages(selectedDataset), [selectedDataset]);
  const currentImage = selectedImageId !== null ? images.find((image) => image.id === selectedImageId) ?? null : selectedDataset?.state === 'completed' ? null : images.find((image) => image.state === 'unseen') ?? null;
  const currentDefectName = getDefectName(defectOptions, selectedDefectType);
  const datasetDefectName = selectedMetadata ? getDefectName(defectOptions, selectedMetadata.defect_type) : '';
  const progress = getEvaluationPrecision(images);
  const remainingInDataset = images.length - progress.evaluated;

  const counters = useMemo(() => {
    const usedIds = new Set((availability?.used_ids ?? []).map(String));
    return {
      total: candidateIds.length,
      evaluated: candidateIds.filter((id) => usedIds.has(id)).length,
      available: candidateIds.filter((id) => !usedIds.has(id)).length,
    };
  }, [candidateIds, availability]);
  const maxSelectableCount = Math.min(500, counters.available);
  const selectedQuantity = Math.min(selectedCount, maxSelectableCount);
  const currentDefectId = currentImage?.annotation_data?.evaluation?.defect_id;
  const currentDefectTimestamp = currentImage ? candidateTimestamps[String(currentDefectId)] : null;
  const currentDefectDetails = candidateDetails[String(currentDefectId)] ?? {};
  const currentLine = currentDefectDetails.line ?? '—';
  const currentCamera = currentDefectDetails.camera ?? '—';

  useEffect(() => {
    setShowMask(true);
    setMaskAvailable(false);
  }, [currentImage?.id]);

  useEffect(() => {
    const strip = thumbnailsRef.current;
    const index = images.findIndex((image) => image.id === currentImage?.id);
    if (!strip || index < 0) return;
    const thumbnail = strip.children[index];
    if (!thumbnail) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();
    strip.scrollTo({ left: strip.scrollLeft + thumbnailRect.left - stripRect.left - (stripRect.width - thumbnailRect.width) / 2, behavior: 'smooth' });
  }, [currentImage?.id, selectedDatasetId, images.length]);

  useEffect(() => {
    let active = true;
    getEvaluationDatasets().then((data) => { if (active) setDatasets(data.filter((dataset) => isEvaluationDataset(dataset, clientId))); })
      .catch((reason) => { if (active) setError(reason.message); })
      .finally(() => { if (active) setIsLoadingDatasets(false); });
    return () => { active = false; };
  }, [clientId]);

  useEffect(() => {
    if (selectedDefectType === '') return;
    let active = true;
    setIsLoadingCandidates(true);
    setAvailability(null);
    setCandidateIds([]);
    setCandidatesError('');

    Promise.all([loadCandidateIds(Number(selectedDefectType)), getEvaluationAvailability(clientId, selectedDefectType)])
      .then(([ids, status]) => {
        if (!active) return;
        setCandidateIds([...new Set(ids.map(getCandidateId).filter((id) => id !== null && id !== undefined && id !== '').map(String))]);
        setCandidateTimestamps((current) => mergeCandidateTimestamps(current, ids));
        setCandidateDetails((current) => mergeCandidateDetails(current, ids));
        setAvailability(status);
      })
      .catch((reason) => { if (active) setCandidatesError(reason.message); })
      .finally(() => { if (active) setIsLoadingCandidates(false); });

    return () => { active = false; };
  }, [clientId, selectedDefectType, loadCandidateIds]);

  useEffect(() => {
    if (!selectedMetadata || !selectedDataset) { setDatasetAvailability(null); return; }
    let active = true;
    setDatasetAvailability(null);
    getEvaluationAvailability(clientId, selectedMetadata.defect_type).then((data) => { if (active) setDatasetAvailability(data); }).catch(() => {});
    return () => { active = false; };
  }, [clientId, selectedDataset?.id, selectedDataset?.state, selectedMetadata?.defect_type]);

  useEffect(() => {
    if (!selectedMetadata || Number(selectedMetadata.defect_type) === Number(selectedDefectType)) return;
    let active = true;
    loadCandidateIds(Number(selectedMetadata.defect_type)).then((candidates) => {
      if (active) {
        setCandidateTimestamps((current) => mergeCandidateTimestamps(current, candidates));
        setCandidateDetails((current) => mergeCandidateDetails(current, candidates));
      }
    }).catch((reason) => console.error('Unable to load detection dates:', reason));
    return () => { active = false; };
  }, [selectedDatasetId, selectedMetadata?.defect_type, selectedDefectType, loadCandidateIds]);

  const openDataset = async (id) => {
    setSelectedDatasetId(id);
    setSelectedImageId(null);
    setError('');
    setIsLoadingDataset(true);

    try {
      const updated = await getEvaluationDataset(id);
      setDatasets((current) => current.map((dataset) => dataset.id === id ? updated : dataset));
    } catch (reason) {
      setError(reason.message);
    } finally {
      setIsLoadingDataset(false);
    }
  };

  const handleCreate = async () => {
    if (isCreating || selectedDefectType === '' || selectedQuantity < 1 || isLoadingCandidates) return;
    setIsCreating(true);
    setError('');

    try {
      const result = await createEvaluationDataset({
        client_id: clientId,
        defect_type: Number(selectedDefectType),
        count: selectedQuantity,
        candidate_ids: candidateIds,
        name: `${currentDefectName} - ${new Date().toLocaleString('es-ES')}`,
      });
      const created = await getEvaluationDataset(result.dataset_id);
      setDatasets((current) => [created, ...current.filter((dataset) => dataset.id !== created.id)]);
      setSelectedDatasetId(created.id);
      setSelectedImageId(null);
      getEvaluationAvailability(clientId, selectedDefectType).then(setAvailability).catch(() => {});
    } catch (reason) {
      setError(reason.status === 409 ? 'Algunas imágenes ya se han reservado. Actualiza la disponibilidad e inténtalo de nuevo.' : reason.message);
      getEvaluationAvailability(clientId, selectedDefectType).then(setAvailability).catch(() => {});
    } finally {
      setIsCreating(false);
    }
  };

  const handleVote = async (state) => {
    if (isSaving || !currentImage) return;
    const wasUnseen = currentImage.state === 'unseen';
    setIsSaving(true);
    setError('');

    try {
      const result = await saveEvaluationImage(currentImage.id, state);
      setDatasets((current) => current.map((dataset) => {
        if (dataset.id !== result.dataset_id) return dataset;
        const updatedImages = dataset.dataset_images.map((image) => image.id === result.id ? { ...image, state: result.state } : image);
        const counts = getEvaluationPrecision(updatedImages);
        return { ...dataset, dataset_images: updatedImages, state: result.completed ? 'completed' : 'in_progress', complete_date: result.completed ? Math.floor(Date.now() / 1000) : dataset.complete_date, viewed_images: counts.evaluated, validated_images: counts.correct };
      }));
      if (wasUnseen) setSelectedImageId(null);
      if (selectedMetadata) getEvaluationAvailability(clientId, selectedMetadata.defect_type).then(setDatasetAvailability).catch(() => {});
    } catch (reason) {
      setError(reason.status === 409 ? 'Esta imagen ya ha sido evaluada por otra persona. La carpeta se ha actualizado.' : reason.message);
      if (reason.status === 409) {
        const updated = await getEvaluationDataset(selectedDatasetId).catch(() => null);
        if (updated) setDatasets((current) => current.map((dataset) => dataset.id === updated.id ? updated : dataset));
        setSelectedImageId(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const sortedDatasets = [...datasets].sort((a, b) => b.creation_date - a.creation_date);

  return (
    <Panel title="EVALUACIÓN" overflowAuto={false}>
      <div className="flex h-full min-h-0 gap-3 p-3">
        <div className="h-full w-72 shrink-0 min-h-0">
          <Panel noHeader rounded category="secondary" overflowAuto={false}>
            <div className="flex h-full min-h-0 flex-col gap-4">
              <ActionButton text="Nueva evaluación" Icon={PlusIcon} onClick={() => { setSelectedDatasetId(null); setSelectedImageId(null); setError(''); }}/>
              <div className="text-xs font-semibold uppercase tracking-wide text-perception-gray-600">Carpetas de evaluación ({sortedDatasets.length})</div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoadingDatasets ? <Loader category="transparent"/> : sortedDatasets.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-perception-gray-700"><InboxIcon className="size-12"/><span className="text-sm">Sin evaluaciones</span></div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {sortedDatasets.map((dataset) => {
                      const summary = getEvaluationPrecision(dataset.dataset_images ?? []);
                      const selected = selectedDatasetId === dataset.id;

                      return (
                        <button key={dataset.id} type="button" onClick={() => openDataset(dataset.id)} className={`flex flex-col gap-2 rounded-md border-2 bg-perception-black-700 p-3 text-left transition-colors ${selected ? 'border-perception-blue' : 'border-transparent hover:border-perception-gray-700'}`}>
                          <div className="truncate text-sm font-medium text-white">{getDatasetTitle(dataset.name)}</div>
                          <div className="flex items-center justify-between gap-2 text-xs text-perception-gray-500">
                            <span>{dataset.state === 'completed' ? `Completado - ${formatPrecision(summary.precision)}` : 'En curso'}</span>
                            <span>{summary.evaluated}/{dataset.total_images ?? dataset.dataset_images?.length ?? 0}</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-perception-black-800"><div className="h-full bg-perception-blue" style={{ width: `${dataset.total_images ? summary.evaluated / dataset.total_images * 100 : 0}%` }}/></div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </Panel>
        </div>

        <div className="h-full min-h-0 min-w-0 flex-1">
          {selectedDatasetId === null ? (
            <Panel noHeader rounded category="secondary" overflowAuto={false}>
              <div className="h-full overflow-y-auto">
                <div className="mx-auto flex min-h-full w-full max-w-4xl flex-col justify-center gap-7 px-5 py-8">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Nueva evaluación</h2>
                    <p className="mt-2 text-sm text-perception-gray-500">Selecciona un tipo de defecto y el número de imágenes para evaluar.</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="evaluation-defect" className="text-xs font-semibold uppercase text-perception-gray-600">Tipo de defecto</label>
                    <select id="evaluation-defect" value={selectedDefectType} onChange={(event) => { setSelectedDefectType(event.target.value); setSelectedCount(10); }} className="w-full rounded-md border border-perception-black-500 bg-perception-black-700 px-3 py-3 text-white outline-none focus:border-perception-blue">
                      {defectOptions.map((defect) => <option key={defect.id} value={defect.id}>{defect.name}</option>)}
                    </select>
                  </div>

                  {isLoadingCandidates ? (
                    <div className="flex min-h-32 items-center justify-center"><Loader category="transparent"/></div>
                  ) : candidatesError ? (
                    <div className="rounded-md bg-red-500/10 p-4 text-sm text-red-400">No se han podido cargar las detecciones: {candidatesError}</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[{ label: 'Detecciones', value: counters.total }, { label: 'Ya evaluadas', value: counters.evaluated }, { label: 'Disponibles', value: counters.available }].map((item) => (
                          <div key={item.label} className="rounded-md bg-perception-black-700 p-4">
                            <div className="text-2xl font-semibold text-white">{item.value}</div>
                            <div className="mt-2 text-xs text-perception-gray-500">{item.label}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-col gap-3">
                        <div className="text-xs font-semibold uppercase text-perception-gray-600">Número de imágenes</div>
                        <div className="flex items-center gap-5">
                          <input type="range" min="0" max={maxSelectableCount} step="1" value={selectedQuantity} disabled={maxSelectableCount === 0} onChange={(event) => setSelectedCount(Number(event.target.value))} className="w-full cursor-pointer accent-perception-blue disabled:cursor-not-allowed disabled:opacity-40"/>
                          <span className="w-14 shrink-0 text-right text-lg font-semibold text-white">{selectedQuantity}</span>
                        </div>
                        <div className="flex justify-between text-xs text-perception-gray-600"><span>0</span><span>{maxSelectableCount}</span></div>
                        <div className="text-xs text-perception-gray-600">{counters.available === 0 ? 'No quedan imágenes disponibles para este defecto.' : `${counters.available} imágenes disponibles para evaluar.`}</div>
                      </div>
                      <div className="flex items-center justify-end gap-4">
                        <ActionButton text={isCreating ? 'Creando...' : `Empezar (${selectedQuantity})`} disabled={isCreating || selectedQuantity < 1} onClick={handleCreate}/>
                      </div>
                    </>
                  )}
                  {error && <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}
                </div>
              </div>
            </Panel>
          ) : isLoadingDataset ? (
            <Panel noHeader rounded category="secondary"><Loader category="transparent"/></Panel>
          ) : !selectedDataset ? (
            <Panel noHeader rounded category="secondary"><div className="flex h-full items-center justify-center text-perception-gray-600">No se ha podido cargar la carpeta.</div></Panel>
          ) : (
            <div className="flex h-full min-h-0 flex-col gap-3">
              <div className="flex shrink-0 items-center justify-between gap-4 rounded-md bg-perception-black-500 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-white">{selectedDataset.name}</div>
                  <div className="mt-1 text-xs text-perception-gray-500">{progress.evaluated}/{images.length} evaluadas - {remainingInDataset} pendientes</div>
                </div>
                <div className="shrink-0 text-sm font-semibold text-perception-gray-300">{formatPrecision(progress.precision)}</div>
              </div>

              <div className="min-h-0 flex-1">
                <Panel noHeader rounded category="secondary" overflowAuto={false}>
                  {currentImage === null && selectedDataset.state === 'completed' ? (
                    <EvaluationScore title={datasetDefectName} images={images} globalPrecision={datasetAvailability?.precision} onBack={() => setSelectedImageId(images[0]?.id ?? null)}/>
                  ) : currentImage === null ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-perception-gray-600"><InboxIcon className="size-12"/><span>Sin imágenes pendientes</span></div>
                  ) : (
                    <div className="flex h-full min-h-0 flex-col gap-3">
                      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-perception-gray-500">
                        <span>Imagen {images.findIndex((image) => image.id === currentImage.id) + 1} / {images.length}</span>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span>Línea: <span className="text-perception-gray-300">{currentLine}</span></span>
                          <span>Cámara: <span className="text-perception-gray-300">{currentCamera}</span></span>
                          <span>{formatDetectionDate(currentDefectTimestamp, timeZone)}</span>
                        </div>
                      </div>
                      <div className="min-h-0 flex-1">
                        <EvaluationImage key={currentImage.id} src={getImageUrl(currentImage.annotation_data?.evaluation?.defect_id)} alt={datasetDefectName} showMask={showMask} onMaskAvailabilityChange={setMaskAvailable}/>
                      </div>
                      {error && <div className="shrink-0 rounded-md bg-red-500/10 p-2 text-center text-sm text-red-400">{error}</div>}
                      <div className="grid shrink-0 grid-cols-1 items-center gap-3 lg:grid-cols-[1fr_auto_1fr]">
                        <div className="hidden lg:block"/>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                          {selectedDataset.state === 'completed' && <ActionButton text="Volver al resumen" Icon={ChevronLeftIcon} color="secondary" onClick={() => setSelectedImageId(null)}/>}
                          {selectedDataset.state !== 'completed' && currentImage.state !== 'unseen' && <ActionButton text="Siguiente pendiente" color="secondary" onClick={() => setSelectedImageId(null)}/>}
                          <button type="button" disabled={isSaving} onClick={() => handleVote('validated')} className={`${voteButtonClass} bg-green-600 hover:bg-green-700`}>
                            <CheckCircleIcon className="size-5"/>Correcto
                          </button>
                          <button type="button" disabled={isSaving} onClick={() => handleVote('error')} className={`${voteButtonClass} bg-red-600 hover:bg-red-700`}>
                            <XCircleIcon className="size-5"/>Incorrecto
                          </button>
                        </div>
                        <button type="button" role="switch" aria-checked={showMask} disabled={!maskAvailable} title={!maskAvailable ? 'Máscara no disponible' : undefined} onClick={() => setShowMask((value) => !value)} className="flex items-center justify-self-center gap-2 text-xs text-perception-gray-400 disabled:cursor-not-allowed disabled:opacity-40 lg:justify-self-end">
                          Mostrar máscara
                          <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showMask ? 'bg-perception-blue' : 'bg-perception-black-500'}`}>
                            <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${showMask ? 'translate-x-[18px]' : 'translate-x-[2px]'}`}/>
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </Panel>
              </div>

              <div className="h-36 shrink-0">
                <Panel noHeader rounded category="secondary" overflowAuto={false}>
                  <div ref={thumbnailsRef} className="flex h-full min-h-0 gap-2 overflow-x-auto">
                    {images.map((image, index) => (
                      <EvaluationThumbnail key={image.id} image={image} index={index} selected={currentImage?.id === image.id} src={getImageUrl(image.annotation_data?.evaluation?.defect_id)} onClick={() => setSelectedImageId(image.id)}/>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};
